import { useApolloClient } from '@apollo/client';
import { BlockEmbed } from 'quill/blots/block';
import React from 'react';
import { useDispatch } from 'react-redux';
import { MenuItem } from '../../../components/Menu';
import mentionIcon from '../../../icons/editor/mention.svg';
import newTabIcon from '../../../icons/editor/newTab.svg';
import viewExpandedIcon from '../../../icons/editor/viewExpanded.svg';
import UIActions from '../../../ui/UIActions';
import { getEmbedPresentationOptions } from '../../embed/EmbedComponent';
import type { EmbedValue } from '../../embed/matchEmbed';
import { EmbedMode, embedUrl } from '../../embed/matchEmbed';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import EDITOR_INTEGRATION_MENTIONS from '../../mentions/EditorIntegrationMentions';
import EditorModal from '../../modals/EditorModal';
import { useQuill } from '../../QuillContext';
import fetchPostAnchorTitle from '../../utils/fetchPostAnchorTitle';
import replaceBlotWith from '../../utils/replaceBlotWith';
import DisplayAsLinkMenuItem from '../DisplayAsLinkMenuItem';
import {
  heightLargelIcon,
  heightMediumIcon,
  heightSmallIcon,
  heightXSmallIcon,
  spanDefaultIcon,
  spanFullIcon,
} from '../editorIcons';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarEditButton from './buttons/ToolbarEditButton';
import ToolbarOverflowMenuButton from './buttons/ToolbarOverflowMenuButton';
import FloatingToolbar from './FloatingToolbar';
import formatBlotWithoutScrolling from './formatBlotWithoutScrolling';

const unaccessibleFullUrl = ['google-map'];

type Formats = Record<string, string[] | string | null | undefined> & {
  comment?: string[] | null;
};

type Value = { embed?: EmbedValue; iframe?: string };

interface BaseEmbedToolbarProps {
  hoveredBlot: BlockEmbed;
  formats: Formats;
  format: (format: { height?: string | null; span?: string | null }) => void;
  publicPost?: boolean;
  setPersist: (persist: boolean) => void;
  value: Value;
  readOnly?: boolean;
}

export const BaseEmbedToolbar = ({
  hoveredBlot,
  formats,
  format,
  publicPost,
  setPersist,
  value,
  readOnly,
}: BaseEmbedToolbarProps) => {
  const { quill } = useQuill();
  const dispatch = useDispatch();
  const apollo = useApolloClient();

  const fullUrl = value.embed
    ? embedUrl(value.embed, EmbedMode.FULL)
    : value.iframe;

  const isFullUrlAccessible =
    !value.embed || !unaccessibleFullUrl.includes(value.embed.type);

  const modalProps = value.embed
    ? {
        embed: {
          id: value.embed.id,
          type: value.embed.type,
          readOnly,
        },
      }
    : { iframe: value.iframe };

  if (publicPost) {
    return (
      <>
        <ToolbarButtonGroup>
          <ToolbarButton
            icon={viewExpandedIcon}
            tooltip="View expanded"
            onClick={() =>
              dispatch(UIActions.openModal(EditorModal, modalProps))
            }
          />
        </ToolbarButtonGroup>
        {!!fullUrl && isFullUrlAccessible && (
          <ToolbarButtonGroup>
            <ToolbarButton
              icon={newTabIcon}
              tooltip="Open in new tab"
              to={fullUrl}
              target="_blank"
            />
          </ToolbarButtonGroup>
        )}
      </>
    );
  }

  const presentationOptions: Partial<
    ReturnType<typeof getEmbedPresentationOptions>
  > = value.embed ? getEmbedPresentationOptions(value.embed.type) : {};

  const canBeRenderedAsMention = Boolean(
    value.embed &&
      (value.embed.type === 'post' ||
        EDITOR_INTEGRATION_MENTIONS[value.embed.type]?.modes.includes(
          'mention',
        )),
  );

  return (
    <>
      {readOnly ? (
        <ToolbarButtonGroup>
          <ToolbarEditButton targetBlot={hoveredBlot} />
        </ToolbarButtonGroup>
      ) : (
        <>
          <ToolbarButtonGroup>
            <ToolbarButton
              icon={spanDefaultIcon}
              tooltip="Default width"
              active={!formats.span}
              onClick={() => format({ span: null })}
            />
            <ToolbarButton
              icon={spanFullIcon}
              tooltip="Full width"
              active={formats.span === 'full'}
              onClick={() => format({ span: 'full' })}
            />
          </ToolbarButtonGroup>
          {presentationOptions.renderMode !== 'image' && (
            <ToolbarButtonGroup>
              {presentationOptions.allowXSmallHeight && (
                <ToolbarButton
                  icon={heightXSmallIcon}
                  tooltip="Short height"
                  active={formats.height === 'xsmall'}
                  onClick={() => format({ height: 'xsmall' })}
                />
              )}
              <ToolbarButton
                icon={heightSmallIcon}
                tooltip="Default height"
                active={!formats.height}
                onClick={() => format({ height: null })}
              />
              <ToolbarButton
                icon={heightMediumIcon}
                tooltip="Medium height"
                active={formats.height === 'medium'}
                onClick={() => format({ height: 'medium' })}
              />
              <ToolbarButton
                icon={heightLargelIcon}
                tooltip="Tall height"
                active={formats.height === 'large'}
                onClick={() => format({ height: 'large' })}
              />
            </ToolbarButtonGroup>
          )}
        </>
      )}
      <ToolbarCommentButtonGroup
        blot={hoveredBlot}
        existingComment={formats.comment}
        onReactionPickerOpenChange={setPersist}
      />
      <ToolbarButtonGroup>
        <ToolbarOverflowMenuButton onMenuOpenChange={setPersist}>
          {(onClose) => (
            <>
              <MenuItem
                icon={viewExpandedIcon}
                onClick={() => {
                  dispatch(UIActions.openModal(EditorModal, modalProps));
                  onClose();
                }}
              >
                View expanded
              </MenuItem>
              {isFullUrlAccessible && fullUrl && (
                <MenuItem
                  icon={newTabIcon}
                  to={fullUrl}
                  target="_blank"
                  onClick={onClose}
                >
                  Open in new tab
                </MenuItem>
              )}
              {quill && canBeRenderedAsMention && (
                <MenuItem
                  icon={mentionIcon}
                  onClick={async () => {
                    let attributes;
                    if (value.embed?.type === 'post') {
                      const title = await fetchPostAnchorTitle(
                        apollo,
                        value.embed.id,
                      );
                      if (title) {
                        attributes = { 'mention-text': title };
                      }
                    }
                    replaceBlotWith(
                      quill,
                      hoveredBlot,
                      {
                        mention: {
                          type: value.embed?.type,
                          id: value.embed?.id,
                        },
                      },
                      attributes,
                    );
                  }}
                  testId="displayAsMentionMenuItem"
                >
                  Display as mention
                </MenuItem>
              )}
              {!readOnly && quill && fullUrl && (
                <DisplayAsLinkMenuItem
                  quill={quill}
                  blot={hoveredBlot}
                  link={fullUrl}
                  onClick={onClose}
                />
              )}
            </>
          )}
        </ToolbarOverflowMenuButton>
      </ToolbarButtonGroup>
    </>
  );
};

type Context = {
  formats: Record<string, string[] | string | null | undefined>;
  value: Value;
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<Context> = {
  getHoveredBlot(blot) {
    return blot.statics.blotName === 'embed' ||
      blot.statics.blotName === 'iframe'
      ? blot
      : null;
  },
  getContext(blot: BlockEmbed) {
    return {
      formats: blot.formats(),
      value: blot.value(),
    };
  },
};

interface EmbedToolbarProps {
  publicPost?: boolean;
}

const EmbedToolbar = ({ publicPost }: EmbedToolbarProps) => {
  const { quill, readOnly } = useQuill();

  const {
    bounds,
    hoveredBlot,
    context: { formats, value } = { formats: undefined, value: undefined },
    setPersist,
  } = useHoveredBlotListener(quill, hoveredBlotListenerSetting);

  return (
    <FloatingToolbar bounds={bounds}>
      {hoveredBlot instanceof BlockEmbed && value && formats && (
        <BaseEmbedToolbar
          hoveredBlot={hoveredBlot}
          formats={formats}
          format={(attributes) => {
            if (!quill) return;
            formatBlotWithoutScrolling({
              quill,
              attributes,
              blot: hoveredBlot,
            });
          }}
          publicPost={publicPost}
          setPersist={setPersist}
          value={value}
          readOnly={readOnly}
        />
      )}
    </FloatingToolbar>
  );
};

export default EmbedToolbar;
